import { Color } from '@persona-id/design-tokens';
import { MouseEvent, MutableRefObject, RefObject } from 'react';

import { usePanZoomBoxControls } from 'dashboard/components-lib/PanZoomBox';
import { InquiryFunnelStep } from 'dashboard/models/inquiry-funnel';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { WORKFLOW_BRANCH_HEIGHT } from 'dashboard/views/DashboardInquiryAnalyticsFunnelV2View/InquiryFunnelStepCard';
import { InquiryFunnelTreeConnector } from 'dashboard/views/DashboardInquiryAnalyticsFunnelV2View/InquiryFunnelTreeConnector';
import { StepCardContainer } from 'dashboard/views/DashboardInquiryAnalyticsFunnelV2View/StepCardContainer';
import InquiryFunnelStepFlow from 'dashboard/views/DashboardInquiryAnalyticsFunnelV2View/StepFlow';

export type FunnelNode = Exclude<InquiryFunnelStep, 'branches'> & {
  // We assign our own unique token to each node so we can have a unique ID for each node
  // in our graph.
  nodeToken: string;
  // This is the % of the current step compared to the start node. Used to render the sankey width.
  pathRatio: number;
  relativeRatio: number;
  branches?: FunnelNode[][];
  parentCondition?: {
    token: string;
    name: string;
    metrics: {};
    pathRatio: number;
    relativeRatio: number;
  };
  dropOffMetrics?: {
    value: number;
    pathRatio: number;
    relativeRatio: number;
  };
};

export interface InquiryFunnelTreeProps {
  steps: FunnelNode[];
  activeNodeToken: string;
  selectNode: (selectedNodeToken: string) => void;
  template: InquiryTemplate;
  selectedPath: FunnelNode[];
  onSelectPath: (
    event: MouseEvent,
    scrollRef: RefObject<HTMLDivElement>,
    selectedPath: FunnelNode[],
  ) => void;
  isAbsolute: boolean;
  inquiryTemplateVersionId: string;
}

const InquiryFunnelTree = ({
  steps,
  activeNodeToken,
  selectNode,
  template,
  selectedPath,
  onSelectPath,
  isAbsolute,
  inquiryTemplateVersionId,
}: InquiryFunnelTreeProps) => {
  const allSteps: FunnelNode[] = steps;
  const { zoomableRef } = usePanZoomBoxControls();

  const renderConnector = (previousStep: FunnelNode, nextStep: FunnelNode) => {
    const selectedNodes = selectedPath.map((path) => path.nodeToken);
    return (
      <InquiryFunnelTreeConnector
        id={`${previousStep.nodeToken}-${nextStep.nodeToken}`}
        lineColor={
          selectedNodes.includes(nextStep.nodeToken) ? Color.CorePurple600 : Color.CoreSlate200
        }
        borderColor={
          selectedNodes.includes(nextStep.nodeToken) ? Color.CorePurple800 : Color.CoreSlate1000
        }
        pathRatio={nextStep.pathRatio}
      />
    );
  };
  return (
    <>
      <InquiryFunnelStepFlow
        innerRef={zoomableRef}
        steps={allSteps}
        stepContentHeight={WORKFLOW_BRANCH_HEIGHT}
        selectedPath={selectedPath}
        renderStep={(
          ref: MutableRefObject<HTMLDivElement>,
          step: FunnelNode,
          currentPath: FunnelNode[],
        ) => {
          return (
            <StepCardContainer
              ref={ref}
              currentStep={step}
              currentPath={[...currentPath, step]}
              activeNodeToken={activeNodeToken}
              selectNode={selectNode}
              template={template}
              selectedPath={selectedPath}
              onSelectPath={onSelectPath}
              isAbsolute={isAbsolute}
              inquiryTemplateVersionId={inquiryTemplateVersionId}
            />
          );
        }}
        renderConnector={renderConnector}
        currentPath={[]}
      />
    </>
  );
};

export default InquiryFunnelTree;
